// ── On-chain audit of paid NIP-05 claims ─────────────────────────
//
// A kind 30078 claim event proves someone *published a claim*. It does
// not prove they paid: the event is self-signed, so anyone can mint one.
// This module checks claims against the Bitcoin Cash chain.
//
// ── What can and cannot be proven ──
//
// The BCH tip wallet is `Wallet.newRandom()` — it is NOT derived from the
// user's Nostr key. So a transaction paying the platform fee address
// carries no cryptographic link back to a pubkey. The chain can tell us
// "this txid paid N sats to the fee address", but not "npub1x paid it".
//
// That gives two different strengths of result:
//
//   • Claims carrying a `fee` tag (txid) — verifiable. We confirm the
//     transaction exists, paid the fee address, and paid enough. The
//     txid → pubkey binding rests on the signed claim event: only the
//     holder of that key could have published this txid under this name,
//     and a txid can only be legitimately cited once (see below).
//
//   • Claims with no `fee` tag — unverifiable, and this is expected for
//     everything published before the txid was recorded. Absence of a
//     tag is NOT evidence of non-payment.
//
// Because a txid is public, someone could copy another user's txid into
// their own claim. `auditClaims` therefore rejects a txid reused across
// different pubkeys, awarding it to the earliest claim.
//
// This is an assistive audit, not an authority. It surfaces claims worth
// a human look; it never silently grants or revokes a badge.

const CORS_PROXY = 'https://proxy.shakespeare.diy/?url=';

/** Fee tolerance. BCH price moves between quote and broadcast, and the
 *  wallet may shave a little for miner fees, so an exact match is wrong. */
const AMOUNT_TOLERANCE = 0.8; // accept >= 80% of expected

export type AuditVerdict =
  /** Transaction found, paid the fee address, amount sufficient. */
  | 'paid'
  /** Transaction found and paid the fee address, but under-paid. */
  | 'underpaid'
  /** Transaction exists but pays nothing to the fee address. */
  | 'wrong-recipient'
  /** Txid cited by an earlier claim from a different pubkey. */
  | 'duplicate-txid'
  /** Txid not found on chain. */
  | 'not-found'
  /** Claim records no txid — predates txid recording. Unknown, not unpaid. */
  | 'no-record'
  /** Chain lookup failed. Transient; retry. */
  | 'unavailable';

export interface AuditResult {
  verdict: AuditVerdict;
  /** Satoshis actually paid to the fee address, when known. */
  paidSats?: number;
  /** Satoshis the claim says it paid. */
  expectedSats?: number;
  /** Block height, or undefined while unconfirmed. */
  blockHeight?: number;
  /** The pubkey that cited this txid first, for 'duplicate-txid'. */
  firstClaimedBy?: string;
}

/** A claim reduced to what the audit needs. */
export interface AuditableClaim {
  name: string;
  pubkey: string;
  claimedAt: number;
  /** Payment txid, if the claim recorded one. */
  txid?: string;
  /** Satoshis the claim says were paid. */
  amountSats?: number;
}

const TXID_RE = /^[0-9a-f]{64}$/;

function normalizeAddress(addr: string): string {
  return addr.replace(/^bitcoincash:/i, '').toLowerCase();
}

interface TxOutputSummary {
  paidSats: number;
  blockHeight?: number;
}

/**
 * Sum the outputs of `txid` that pay `feeAddress`.
 *
 * Returns null when the transaction can't be fetched, so callers can
 * distinguish "not found" from "found but wrong". Tries two independent
 * explorers, matching the resilience approach in useTipWallet.
 */
async function fetchTxPayment(
  txid: string,
  feeAddress: string,
): Promise<TxOutputSummary | null> {
  const target = normalizeAddress(feeAddress);

  // 1) bchexplorer.cash — Blockstream-style shape.
  try {
    const url = encodeURIComponent(`https://bchexplorer.cash/api/v1/tx/${txid}`);
    const res = await fetch(`${CORS_PROXY}${url}`, { signal: AbortSignal.timeout(10000) });
    if (res.ok) {
      const json = await res.json();
      const outs = Array.isArray(json?.vout) ? json.vout : null;
      if (outs) {
        let paidSats = 0;
        for (const o of outs) {
          const addr = normalizeAddress(String(o?.scriptpubkey_address || o?.address || ''));
          if (addr && addr === target) paidSats += Number(o?.value ?? 0);
        }
        const height = json?.status?.block_height;
        return { paidSats, blockHeight: typeof height === 'number' ? height : undefined };
      }
    }
  } catch { /* fall through to the next explorer */ }

  // 2) blockchair — different shape, same question.
  try {
    const url = encodeURIComponent(
      `https://api.blockchair.com/bitcoin-cash/dashboards/transaction/${txid}`,
    );
    const res = await fetch(`${CORS_PROXY}${url}`, { signal: AbortSignal.timeout(10000) });
    if (res.ok) {
      const json = await res.json();
      const data = json?.data?.[txid];
      const outs = Array.isArray(data?.outputs) ? data.outputs : null;
      if (outs) {
        let paidSats = 0;
        for (const o of outs) {
          const addr = normalizeAddress(String(o?.recipient || ''));
          if (addr && addr === target) paidSats += Number(o?.value ?? 0);
        }
        const height = data?.transaction?.block_id;
        return {
          paidSats,
          blockHeight: typeof height === 'number' && height > 0 ? height : undefined,
        };
      }
    }
  } catch { /* both explorers failed */ }

  return null;
}

/**
 * Verify a txid actually pays a given BCH address on-chain.
 *
 * Used to check that a self-published tip receipt (kind 9735, which carries a
 * `txid`) corresponds to a REAL transaction that paid the tipped recipient —
 * the faucet counts tips from these receipts, and without this a user can mint
 * fake receipts to farm rewards without sending any BCH.
 *
 * Returns:
 *   · { paid: true,  sats } when the tx exists and pays `address` (sats > 0),
 *   · { paid: false, sats: 0 } when the tx exists but pays `address` nothing,
 *   · null when neither explorer could be reached (transient — retry later).
 */
export async function verifyTxPaysAddress(
  txid: string,
  address: string,
): Promise<{ paid: boolean; sats: number } | null> {
  const clean = (txid || '').trim().toLowerCase();
  if (!TXID_RE.test(clean) || !address) return { paid: false, sats: 0 };
  const summary = await fetchTxPayment(clean, address);
  if (!summary) return null; // couldn't reach explorers
  return { paid: summary.paidSats > 0, sats: summary.paidSats };
}

/**
 * Audit one claim against the chain.
 *
 * `expectedSats` lets the caller supply the fee that *should* have been
 * paid; when omitted the claim's own figure is used.
 */
export async function auditClaim(
  claim: AuditableClaim,
  feeAddress: string,
  expectedSats?: number,
): Promise<AuditResult> {
  const txid = claim.txid?.trim().toLowerCase();
  if (!txid || !TXID_RE.test(txid)) return { verdict: 'no-record' };

  const summary = await fetchTxPayment(txid, feeAddress);
  if (!summary) return { verdict: 'not-found' };

  const expected = expectedSats ?? claim.amountSats;
  const result: AuditResult = {
    verdict: 'paid',
    paidSats: summary.paidSats,
    expectedSats: expected,
    blockHeight: summary.blockHeight,
  };

  if (summary.paidSats <= 0) return { ...result, verdict: 'wrong-recipient' };
  if (expected && summary.paidSats < expected * AMOUNT_TOLERANCE) {
    return { ...result, verdict: 'underpaid' };
  }
  return result;
}

/**
 * Audit a batch of claims, rejecting txids reused across pubkeys.
 *
 * Claims are processed oldest-first so the earliest citation of a txid
 * wins and any later reuse by a *different* pubkey is flagged. The same
 * pubkey citing one txid twice is fine — that's a re-publish, not fraud.
 *
 * Requests are issued in small batches to avoid hammering the explorers.
 */
export async function auditClaims(
  claims: AuditableClaim[],
  feeAddress: string,
  expectedSats?: number,
  onProgress?: (done: number, total: number) => void,
): Promise<Map<string, AuditResult>> {
  const ordered = [...claims].sort((a, b) => a.claimedAt - b.claimedAt);
  const results = new Map<string, AuditResult>();

  // txid → pubkey that cited it first.
  const txidOwner = new Map<string, string>();
  const toCheck: AuditableClaim[] = [];

  for (const claim of ordered) {
    const txid = claim.txid?.trim().toLowerCase();
    if (!txid || !TXID_RE.test(txid)) {
      results.set(claim.name, { verdict: 'no-record' });
      continue;
    }
    const owner = txidOwner.get(txid);
    if (owner && owner !== claim.pubkey) {
      results.set(claim.name, { verdict: 'duplicate-txid', firstClaimedBy: owner });
      continue;
    }
    if (!owner) txidOwner.set(txid, claim.pubkey);
    toCheck.push(claim);
  }

  const CONCURRENCY = 4;
  let done = 0;
  for (let i = 0; i < toCheck.length; i += CONCURRENCY) {
    const slice = toCheck.slice(i, i + CONCURRENCY);
    const settled = await Promise.all(
      slice.map(c =>
        auditClaim(c, feeAddress, expectedSats)
          .catch((): AuditResult => ({ verdict: 'unavailable' })),
      ),
    );
    slice.forEach((c, idx) => results.set(c.name, settled[idx]));
    done += slice.length;
    onProgress?.(done, toCheck.length);
  }

  return results;
}

/** Human-readable label for a verdict. */
export function describeVerdict(v: AuditVerdict): string {
  switch (v) {
    case 'paid': return 'Payment confirmed on-chain';
    case 'underpaid': return 'Paid less than the required fee';
    case 'wrong-recipient': return 'Transaction does not pay the fee address';
    case 'duplicate-txid': return 'Reuses a txid already cited by another account';
    case 'not-found': return 'Transaction not found on-chain';
    case 'no-record': return 'No payment recorded (predates txid tracking)';
    case 'unavailable': return 'Could not reach a block explorer';
  }
}

/** Does this verdict warrant operator attention? */
export function isSuspicious(v: AuditVerdict): boolean {
  return v === 'underpaid' || v === 'wrong-recipient'
    || v === 'duplicate-txid' || v === 'not-found';
}
